import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import type { UserRole } from '../types/database';

interface PrivateRouteProps {
  children: React.ReactNode;
  role?: UserRole;
  roles?: UserRole[];
  permission?: string;
  permissions?: string[];
  requireAllPermissions?: boolean;
  fallbackPath?: string;
}

/**
 * PrivateRoute component that protects routes based on authentication, roles, and permissions.
 *
 * @param children - The component(s) to render if access is granted
 * @param role - Single role required to access the route
 * @param roles - Array of roles, user must have one of them
 * @param permission - Single permission required to access the route
 * @param permissions - Array of permissions to check
 * @param requireAllPermissions - If true, requires all permissions in the array (default: false, requires any)
 * @param fallbackPath - Path to redirect to if access is denied (default: '/signin')
 *
 * @example
 * // Basic authentication check
 * <PrivateRoute>
 *   <Dashboard />
 * </PrivateRoute>
 *
 * @example
 * // Require admin role
 * <PrivateRoute role="admin">
 *   <AdminPanel />
 * </PrivateRoute>
 *
 * @example
 * // Require specific permission
 * <PrivateRoute permission="reports:generate">
 *   <ReportGenerator />
 * </PrivateRoute>
 *
 * @example
 * // Require any of multiple permissions
 * <PrivateRoute permissions={['emissions:update:own', 'emissions:update:all']}>
 *   <EmissionEditor />
 * </PrivateRoute>
 */
export default function PrivateRoute({
  children,
  role,
  roles,
  permission,
  permissions,
  requireAllPermissions = false,
  fallbackPath = '/signin',
}: PrivateRouteProps) {
  const {
    isAuthenticated,
    isLoading,
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  // Check authentication
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check role if specified
  if (role && !hasRole(role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check roles if specified (user must have at least one)
  if (roles && roles.length > 0 && !hasAnyRole(roles)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check single permission if specified
  if (permission && !hasPermission(permission)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check permissions if specified
  if (permissions && permissions.length > 0) {
    const hasAccess = requireAllPermissions
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    if (!hasAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // All checks passed, render children
  return <>{children}</>;
}
